import { useState, useEffect, useCallback } from 'react';
import { Note, CreateNoteRequest, UpdateNoteRequest } from '../types/note';
import { api } from '../services/api';

export function useNotes() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getNotes();
      setNotes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, []);

  const addNote = async (request: CreateNoteRequest) => {
    try {
      const newNote = await api.createNote(request);
      setNotes(prev => [newNote, ...prev]);
      return newNote;
    } catch (err) {
      throw err;
    }
  };

  const updateNote = async (id: number, request: UpdateNoteRequest) => {
    try {
      const updatedNote = await api.updateNote(id, request);
      setNotes(prev => prev.map(n => n.id === id ? updatedNote : n));
      return updatedNote;
    } catch (err) {
      throw err;
    }
  };

  const deleteNote = async (id: number) => {
    try {
      await api.deleteNote(id);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return {
    notes,
    loading,
    error,
    refresh: fetchNotes,
    addNote,
    updateNote,
    deleteNote
  };
}
